import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Play, Pause, Square, Minus, Plus, Mic2, Volume2, VolumeX, ScanFace, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

// Same map as ScriptEditor — keep in sync if products change.
const PRODUCT_DURATION = {
    entry_ai: 45,
    basic_ai: 80,
    standard: 120,
    deluxe: 300,
};

export default function ScriptReader() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [req, setReq] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fontSize, setFontSize] = useState(() => {
        const saved = parseInt(localStorage.getItem("vsf_script_fontsize") || "44");
        return Number.isFinite(saved) ? saved : 44;
    });

    // ---------- Teleprompter (auto-scroll) ----------
    const [running, setRunning] = useState(false);
    const [started, setStarted] = useState(false); // true after first play (until Stop)
    const [elapsed, setElapsed] = useState(0); // seconds since start
    const startedAtRef = useRef(null);
    const rafRef = useRef(null);
    const scrollerRef = useRef(null);

    // ---------- Optional TTS (off by default for teleprompter use) ----------
    const [ttsEnabled, setTtsEnabled] = useState(() => localStorage.getItem("vsf_script_tts") === "1");
    const [voices, setVoices] = useState([]);
    const [voiceURI, setVoiceURI] = useState(() => localStorage.getItem("vsf_script_voice") || "");
    const [rate, setRate] = useState(() => parseFloat(localStorage.getItem("vsf_script_rate") || "1") || 1);

    useEffect(() => {
        api.get(`/requests/${id}`)
            .then((r) => { setReq(r.data); })
            .catch(() => toast.error("Aanvraag niet gevonden"))
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        const loadVoices = () => {
            const v = window.speechSynthesis?.getVoices?.() || [];
            const nl = v.filter((x) => x.lang?.toLowerCase().startsWith("nl"));
            const list = nl.length ? nl : v;
            setVoices(list);
            if (!voiceURI && list.length) {
                const preferred = list.find((x) => /google|microsoft|xander|ellen|claire/i.test(x.name)) || list[0];
                setVoiceURI(preferred.voiceURI);
            }
        };
        loadVoices();
        if (typeof window !== "undefined" && "speechSynthesis" in window) {
            window.speechSynthesis.onvoiceschanged = loadVoices;
        }
        return () => {
            if (typeof window !== "undefined" && "speechSynthesis" in window) {
                window.speechSynthesis.cancel();
                window.speechSynthesis.onvoiceschanged = null;
            }
        };
    // eslint-disable-next-line
    }, []);

    useEffect(() => { localStorage.setItem("vsf_script_fontsize", String(fontSize)); }, [fontSize]);
    useEffect(() => { if (voiceURI) localStorage.setItem("vsf_script_voice", voiceURI); }, [voiceURI]);
    useEffect(() => { localStorage.setItem("vsf_script_rate", String(rate)); }, [rate]);
    useEffect(() => { localStorage.setItem("vsf_script_tts", ttsEnabled ? "1" : "0"); }, [ttsEnabled]);

    const script = req?.script || "";
    const scriptBlocks = req?.script_blocks || [];

    const paragraphs = useMemo(() => {
        if (scriptBlocks.length) return scriptBlocks;
        return script.split(/\n\s*\n/).map((p, i) => ({ id: `legacy-${i}`, text: p.trim(), author_role: null })).filter((b) => b.text);
    }, [script, scriptBlocks]);

    // ---- Inline bewerken in opnamemodus (tablet-vriendelijk) ----
    // Elke alinea is ALTIJD een echte textarea (gestyled als gewone tekst).
    // Een tik is dus native focus op een editable element → toetsenbord opent
    // gegarandeerd op Android/iPad, zonder focus-proxy hacks.
    const [editBlocks, setEditBlocks] = useState(null); // null = niet aan het bewerken
    const [editIdx, setEditIdx] = useState(null);
    const blockRefs = useRef(new Map());
    const displayBlocks = editBlocks || paragraphs;

    const autosize = (el) => {
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    };
    // Enkel her-meten bij fontwijziging of een nieuw script — NIET bij elke
    // toetsaanslag/focus, anders klapt de scroller even dicht en springt de
    // scrollpositie terug naar boven.
    useEffect(() => {
        blockRefs.current.forEach((el) => autosize(el));
    }, [fontSize, paragraphs]);

    const beginEdit = (idx, key) => {
        if (running) return;
        setEditBlocks((prev) => prev || paragraphs.map((b) => ({ ...b })));
        setEditIdx(idx);
        // Synchrone focus binnen de tik zelf (textarea is al gemount) →
        // Android/iPad opent het toetsenbord gegarandeerd.
        const el = blockRefs.current.get(key);
        if (el) {
            try { el.focus({ preventScroll: true }); } catch { el.focus(); }
        }
    };

    const saveInlineEdit = async () => {
        setEditIdx(null);
        if (!editBlocks) return;
        const cleaned = editBlocks.map((b) => ({ ...b, text: (b.text || "").trim() })).filter((b) => b.text);
        const changed = cleaned.length !== paragraphs.length || cleaned.some((b, i) => b.text !== paragraphs[i]?.text);
        if (!changed) { setEditBlocks(null); return; }
        try {
            const payload = cleaned.map((b) => (String(b.id || "").startsWith("legacy-") ? { text: b.text } : { id: b.id, text: b.text }));
            const { data } = await api.patch(`/requests/${id}/script`, { blocks: payload });
            setReq(data);
            setEditBlocks(null);
            toast.success("Script opgeslagen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Opslaan mislukt");
        }
    };

    // Compute target duration: longest product's max duration, else fallback to
    // the spoken-time estimate (Dutch ≈ 150 wpm = 2.5 words/sec). Stored in
    // localStorage as a per-user override so makelaars can tune it for their
    // own reading pace.
    const baseDurationSec = useMemo(() => {
        const items = req?.items || [];
        let best = 0;
        for (const it of items) {
            const d = PRODUCT_DURATION[it.product_id];
            if (d && d > best) best = d;
        }
        if (best) return best;
        const words = (script || "").trim().split(/\s+/).filter(Boolean).length;
        return Math.max(15, Math.round(words / 2.5));
    }, [req, script]);

    const [durationOverride, setDurationOverride] = useState(() => {
        const saved = parseInt(localStorage.getItem(`vsf_script_dur_${id}`) || "0");
        return Number.isFinite(saved) && saved > 0 ? saved : 0;
    });
    const durationSec = durationOverride || baseDurationSec;
    useEffect(() => {
        if (durationOverride) localStorage.setItem(`vsf_script_dur_${id}`, String(durationOverride));
    }, [durationOverride, id]);

    // Min/sec duur-kiezer — klik op de tijdsduur onderaan opent deze picker.
    const [durPickerOpen, setDurPickerOpen] = useState(false);
    const [durMin, setDurMin] = useState(0);
    const [durSecs, setDurSecs] = useState(0);
    const openDurPicker = () => {
        setDurMin(Math.floor(durationSec / 60));
        setDurSecs(durationSec % 60);
        setDurPickerOpen(true);
    };
    const applyDurPicker = () => {
        const total = (parseInt(durMin, 10) || 0) * 60 + (parseInt(durSecs, 10) || 0);
        if (total < 10 || total > 1800) {
            toast.error("Geef een duur tussen 0:10 en 30:00");
            return;
        }
        setDurationOverride(total);
        setDurPickerOpen(false);
    };

    // ---- Auto-scroll loop ----
    const tick = useCallback(() => {
        if (!running || !startedAtRef.current || !scrollerRef.current) return;
        const now = performance.now();
        const elapsedSec = (now - startedAtRef.current) / 1000;
        setElapsed(elapsedSec);
        const el = scrollerRef.current;
        const scrollable = el.scrollHeight - el.clientHeight;
        if (scrollable > 0) {
            const pct = Math.min(1, elapsedSec / durationSec);
            el.scrollTop = pct * scrollable;
            if (pct >= 1) {
                setRunning(false);
                return;
            }
        } else {
            // Nothing to scroll (script fits in viewport) — just stop the clock at the end
            if (elapsedSec >= durationSec) {
                setRunning(false);
                return;
            }
        }
        rafRef.current = requestAnimationFrame(tick);
    }, [running, durationSec]);

    useEffect(() => {
        if (!running) return undefined;
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [running, tick]);

    // If the duration changes while running, rebase the clock on the current
    // scroll position so the scroll speed adjusts without jumping.
    useEffect(() => {
        if (!running || !scrollerRef.current) return;
        const el = scrollerRef.current;
        const scrollable = el.scrollHeight - el.clientHeight;
        const pct = scrollable > 0 ? el.scrollTop / scrollable : 0;
        startedAtRef.current = performance.now() - pct * durationSec * 1000;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [durationSec]);

    // While paused (or before start) the user can scroll to any sentence —
    // keep the timer/progress bar in sync so play resumes from THAT spot.
    const onManualScroll = () => {
        if (running) return; // programmatic scroll from tick()
        const el = scrollerRef.current;
        if (!el) return;
        const scrollable = el.scrollHeight - el.clientHeight;
        if (scrollable <= 0) return;
        setElapsed(Math.min(1, el.scrollTop / scrollable) * durationSec);
    };

    // Play from wherever the user scrolled to (fresh start AND resume). The
    // clock is derived from the scroll position, never from stale state — so
    // "scroll naar een zin en druk op play" continues exactly from that line.
    const playFromHere = () => {
        if (editIdx !== null) {
            try { document.activeElement?.blur(); } catch {}
        }
        if (!script.trim() && paragraphs.length === 0) {
            toast.error("Geen script om voor te lezen");
            return;
        }
        const el = scrollerRef.current;
        const scrollable = el ? el.scrollHeight - el.clientHeight : 0;
        const pct = scrollable > 0 ? Math.min(1, el.scrollTop / scrollable) : 0;
        const startElapsed = pct * durationSec;
        startedAtRef.current = performance.now() - startElapsed * 1000;
        setElapsed(startElapsed);
        setStarted(true);
        setRunning(true);
        if (ttsEnabled && "speechSynthesis" in window) {
            if (window.speechSynthesis.paused) {
                window.speechSynthesis.resume();
            } else if (startElapsed === 0) {
                window.speechSynthesis.cancel();
                const u = new SpeechSynthesisUtterance(script);
                const v = voices.find((x) => x.voiceURI === voiceURI);
                if (v) u.voice = v;
                u.lang = v?.lang || "nl-BE";
                u.rate = rate;
                window.speechSynthesis.speak(u);
            }
        }
    };

    // ---- Spiegel (front-camera check vóór de opname) ----
    const [mirrorOpen, setMirrorOpen] = useState(false);
    const mirrorVideoRef = useRef(null);
    const mirrorStreamRef = useRef(null);

    // Knijp-zoom in de spiegel (1×–4×) + pannen als er ingezoomd is
    const [mirrorZoom, setMirrorZoom] = useState(1);
    const [mirrorPan, setMirrorPan] = useState({ x: 0, y: 0 });
    const mirrorBoxRef = useRef(null);
    const gestureRef = useRef(null);
    const lastTapRef = useRef(0);

    const clampPan = (pan, zoom) => {
        const box = mirrorBoxRef.current?.getBoundingClientRect();
        if (!box) return pan;
        const maxX = ((zoom - 1) * box.width) / 2;
        const maxY = ((zoom - 1) * box.height) / 2;
        return { x: Math.max(-maxX, Math.min(maxX, pan.x)), y: Math.max(-maxY, Math.min(maxY, pan.y)) };
    };

    const setZoomTo = (zoom) => {
        const z = Math.max(1, Math.min(4, zoom));
        setMirrorZoom(z);
        setMirrorPan((p) => (z === 1 ? { x: 0, y: 0 } : clampPan(p, z)));
    };

    const touchDist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

    const onMirrorTouchStart = (e) => {
        const t = e.touches;
        if (t.length === 2) {
            gestureRef.current = { type: "pinch", startDist: touchDist(t), startZoom: mirrorZoom, startPan: { ...mirrorPan } };
        } else if (t.length === 1) {
            const now = Date.now();
            if (now - lastTapRef.current < 300) {
                // Dubbel-tik: wissel tussen 1× en 2.5×
                setZoomTo(mirrorZoom > 1 ? 1 : 2.5);
                lastTapRef.current = 0;
                gestureRef.current = null;
                return;
            }
            lastTapRef.current = now;
            gestureRef.current = mirrorZoom > 1 ? { type: "pan", startX: t[0].clientX, startY: t[0].clientY, startPan: { ...mirrorPan } } : null;
        }
    };

    const onMirrorTouchMove = (e) => {
        const g = gestureRef.current;
        if (!g) return;
        e.preventDefault();
        const t = e.touches;
        if (g.type === "pinch" && t.length === 2) {
            const z = Math.max(1, Math.min(4, g.startZoom * (touchDist(t) / g.startDist)));
            setMirrorZoom(z);
            setMirrorPan(clampPan(g.startPan, z));
        } else if (g.type === "pan" && t.length === 1) {
            setMirrorPan(clampPan({ x: g.startPan.x + (t[0].clientX - g.startX), y: g.startPan.y + (t[0].clientY - g.startY) }, mirrorZoom));
        }
    };

    const onMirrorTouchEnd = (e) => {
        if (e.touches.length === 0) gestureRef.current = null;
        else if (e.touches.length === 1 && gestureRef.current?.type === "pinch") {
            gestureRef.current = mirrorZoom > 1 ? { type: "pan", startX: e.touches[0].clientX, startY: e.touches[0].clientY, startPan: { ...mirrorPan } } : null;
        }
    };

    const openMirror = async () => {
        setMirrorOpen(true);
        setMirrorZoom(1);
        setMirrorPan({ x: 0, y: 0 });
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
                audio: false,
            });
            mirrorStreamRef.current = stream;
            if (mirrorVideoRef.current) {
                mirrorVideoRef.current.srcObject = stream;
                mirrorVideoRef.current.play().catch(() => {});
            }
        } catch (e) {
            setMirrorOpen(false);
            toast.error("Camera niet beschikbaar — geef cameratoegang in de browser");
        }
    };

    const closeMirror = () => {
        if (mirrorStreamRef.current) {
            mirrorStreamRef.current.getTracks().forEach((t) => t.stop());
            mirrorStreamRef.current = null;
        }
        if (mirrorVideoRef.current) mirrorVideoRef.current.srcObject = null;
        setMirrorOpen(false);
    };

    useEffect(() => () => {
        if (mirrorStreamRef.current) mirrorStreamRef.current.getTracks().forEach((t) => t.stop());
    }, []);

    const pauseTeleprompter = () => {
        setRunning(false);
        if (ttsEnabled && "speechSynthesis" in window) window.speechSynthesis.pause();
    };

    const stopTeleprompter = () => {
        setRunning(false);
        setStarted(false);
        setElapsed(0);
        startedAtRef.current = null;
        if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
        if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    };

    if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-500">Laden...</div>;
    if (!req) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-500">Niet gevonden</div>;

    const fmt = (s) => {
        const t = Math.max(0, Math.round(s));
        const m = Math.floor(t / 60);
        const r = t % 60;
        return `${m}:${String(r).padStart(2, "0")}`;
    };
    const remaining = Math.max(0, durationSec - elapsed);
    const progressPct = durationSec > 0 ? Math.min(100, (elapsed / durationSec) * 100) : 0;

    return (
        <div className="h-[100dvh] bg-[#050505] text-white font-body flex flex-col overflow-hidden" data-testid="script-reader-page">
            {/* Header */}
            <header className="flex-shrink-0 bg-[#050505]/95 backdrop-blur-xl border-b border-white/10">
                <div className="max-w-5xl mx-auto px-4 md:px-6 h-14 flex items-center justify-between gap-3">
                    <button
                        onClick={() => navigate(`/projects/${id}`)}
                        className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white active:scale-95 transition"
                        data-testid="script-back-btn"
                    >
                        <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Terug naar project</span>
                    </button>
                    <div className="flex items-center gap-2 text-xs text-zinc-500 truncate">
                        <Mic2 className="h-3.5 w-3.5 text-[#E5A93D] flex-shrink-0" />
                        <span className="truncate">{req.address_street}</span>
                    </div>
                    <div className="font-mono text-xs text-zinc-300 tabular-nums" data-testid="script-timer">
                        <span className="text-[#E5A93D]">{fmt(elapsed)}</span> / {fmt(durationSec)}
                    </div>
                </div>
                {/* Progress bar */}
                <div className="relative h-0.5 bg-white/5 overflow-hidden">
                    <div
                        className="absolute inset-y-0 left-0 bg-[#E5A93D] transition-[width] duration-150 ease-linear"
                        style={{ width: `${progressPct}%` }}
                        data-testid="script-progress"
                    />
                </div>
            </header>

            {/* Scrolling script body */}
            <main
                ref={scrollerRef}
                onScroll={onManualScroll}
                className="flex-1 max-w-5xl mx-auto w-full px-4 md:px-6 overflow-y-auto teleprompter-scroll"
                data-testid="script-scroll"
            >
                {displayBlocks.length > 0 ? (
                    <>
                        {/* Top padding so the first line starts comfortably in view */}
                        <div className="h-[35vh]" aria-hidden="true" />
                        <div
                            className="text-white font-body leading-relaxed select-text"
                            style={{ fontSize: `${fontSize}px`, lineHeight: 1.5 }}
                            data-testid="script-content"
                        >
                            {displayBlocks.map((b, i) => (
                                <div
                                    key={b.id || i}
                                    onClick={() => beginEdit(i, b.id || i)}
                                    className={running || editIdx === i ? "" : "cursor-text"}
                                    data-testid={`script-block-wrap-${b.id || i}`}
                                >
                                    <textarea
                                        ref={(el) => {
                                            if (el) blockRefs.current.set(b.id || i, el);
                                            else blockRefs.current.delete(b.id || i);
                                        }}
                                        value={b.text}
                                        rows={1}
                                        readOnly={running}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setEditBlocks((prev) => (prev || paragraphs.map((x) => ({ ...x }))).map((x, j) => (j === i ? { ...x, text: v } : x)));
                                            autosize(e.target);
                                        }}
                                        onBlur={() => { setEditIdx(null); if (!running) saveInlineEdit(); }}
                                        className={`block w-full mb-10 resize-none outline-none overflow-hidden rounded-md p-2 -ml-2 text-white bg-transparent border transition-colors ${
                                            editIdx === i && !running
                                                ? "bg-white/5 border-[#E5A93D]/50"
                                                : "border-transparent pointer-events-none"
                                        }`}
                                        style={{ fontSize: `${fontSize}px`, lineHeight: 1.5, fontFamily: "inherit" }}
                                        title={running ? undefined : "Tik om te bewerken"}
                                        data-testid={`script-block-${b.id || i}`}
                                    />
                                </div>
                            ))}
                            {!running && editIdx === null && (
                                <p className="text-xs text-zinc-600 -mt-4 mb-6 select-none">✏️ Tik op een alinea om ze direct aan te passen</p>
                            )}
                        </div>
                        {/* Bottom padding so the last line scrolls fully into view */}
                        <div className="h-[60vh]" aria-hidden="true" />
                    </>
                ) : (
                    <div className="text-center text-zinc-500 border border-dashed border-white/10 rounded-md py-20 px-6 my-12" data-testid="script-empty">
                        <ScrollTextIcon />
                        <p className="mt-4 text-sm">Nog geen script beschikbaar.</p>
                        <Link to={`/projects/${id}`} className="inline-block mt-4 text-[#E5A93D] text-sm hover:underline">
                            Ga terug en voeg er een toe
                        </Link>
                    </div>
                )}
            </main>

            {/* Sticky controls dock */}
            {paragraphs.length > 0 && (
                <div className="flex-shrink-0 border-t border-white/10 bg-[#050505]/95 backdrop-blur-xl safe-bottom">
                    <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2" data-testid="prompter-controls">
                            {!started ? (
                                <Button onClick={playFromHere} className="h-11 px-5 bg-[#E5A93D] hover:bg-[#FBBF24] text-black font-bold rounded-sm active:scale-95" data-testid="prompter-start">
                                    <Play className="h-4 w-4 mr-1.5" fill="currentColor" /> Start
                                </Button>
                            ) : (
                                <>
                                    <Button onClick={running ? pauseTeleprompter : playFromHere} className="h-11 px-4 bg-[#E5A93D] hover:bg-[#FBBF24] text-black font-bold rounded-sm active:scale-95" data-testid="prompter-pause">
                                        {running ? <><Pause className="h-4 w-4 mr-1.5" fill="currentColor" />Pauze</> : <><Play className="h-4 w-4 mr-1.5" fill="currentColor" />Hervat</>}
                                    </Button>
                                    <Button onClick={stopTeleprompter} variant="ghost" className="h-11 px-3 border border-white/10 text-zinc-300 hover:bg-white/5 rounded-sm" data-testid="prompter-stop" aria-label="Stop">
                                        <Square className="h-4 w-4" fill="currentColor" />
                                    </Button>
                                </>
                            )}
                            {/* Duur-kiezer — klik op de tijd en stel minuten + seconden in */}
                            {durPickerOpen ? (
                                <div className="flex items-center gap-1.5 flex-wrap" data-testid="prompter-duration-picker">
                                    <div className="flex items-center bg-[#0F0F11] border border-[#E5A93D]/50 rounded-sm h-11 overflow-hidden">
                                        <button type="button" onClick={() => setDurMin((m) => Math.max(0, (parseInt(m, 10) || 0) - 1))} className="h-11 w-9 text-zinc-300 hover:bg-white/10 active:bg-white/15 text-lg font-bold" data-testid="dur-min-dec">−</button>
                                        <input
                                            type="number" min="0" max="30" inputMode="numeric"
                                            value={durMin}
                                            onChange={(e) => setDurMin(e.target.value.replace(/\D/g, "").slice(0, 2))}
                                            onFocus={(e) => e.target.select()}
                                            className="w-10 h-11 bg-transparent text-center text-white font-bold tabular-nums focus:outline-none"
                                            data-testid="dur-min-input"
                                        />
                                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 pr-1">min</span>
                                        <button type="button" onClick={() => setDurMin((m) => Math.min(30, (parseInt(m, 10) || 0) + 1))} className="h-11 w-9 text-zinc-300 hover:bg-white/10 active:bg-white/15 text-lg font-bold" data-testid="dur-min-inc">+</button>
                                    </div>
                                    <div className="flex items-center bg-[#0F0F11] border border-[#E5A93D]/50 rounded-sm h-11 overflow-hidden">
                                        <button type="button" onClick={() => setDurSecs((s) => Math.max(0, (parseInt(s, 10) || 0) - 5))} className="h-11 w-9 text-zinc-300 hover:bg-white/10 active:bg-white/15 text-lg font-bold" data-testid="dur-sec-dec">−</button>
                                        <input
                                            type="number" min="0" max="59" inputMode="numeric"
                                            value={durSecs}
                                            onChange={(e) => setDurSecs(e.target.value.replace(/\D/g, "").slice(0, 2))}
                                            onFocus={(e) => e.target.select()}
                                            className="w-10 h-11 bg-transparent text-center text-white font-bold tabular-nums focus:outline-none"
                                            data-testid="dur-sec-input"
                                        />
                                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 pr-1">sec</span>
                                        <button type="button" onClick={() => setDurSecs((s) => Math.min(59, (parseInt(s, 10) || 0) + 5))} className="h-11 w-9 text-zinc-300 hover:bg-white/10 active:bg-white/15 text-lg font-bold" data-testid="dur-sec-inc">+</button>
                                    </div>
                                    <Button onClick={applyDurPicker} className="h-11 px-4 bg-[#E5A93D] hover:bg-[#FBBF24] text-black font-bold rounded-sm" data-testid="dur-apply">
                                        OK
                                    </Button>
                                    <button type="button" onClick={() => setDurPickerOpen(false)} className="h-11 px-2 text-zinc-500 hover:text-white text-sm" data-testid="dur-cancel">
                                        ✕
                                    </button>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={openDurPicker}
                                    className="h-11 inline-flex items-center gap-2 bg-[#0F0F11] border border-white/10 text-sm text-zinc-300 rounded-sm px-3 hover:border-[#E5A93D]/60 hover:text-white active:scale-95 transition tabular-nums"
                                    data-testid="prompter-duration"
                                    title="Duur van scrollen — klik om minuten en seconden in te stellen"
                                >
                                    <span className="text-[#E5A93D] font-bold">{fmt(durationSec)}</span>
                                    <span className="text-[10px] uppercase tracking-wider text-zinc-500">{durationOverride ? "duur" : "auto"}</span>
                                </button>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            {/* Spiegel — front-camera check voor de opname */}
                            <Button
                                onClick={openMirror}
                                variant="ghost"
                                className="h-11 px-3 border border-white/10 text-zinc-300 hover:bg-white/5 hover:text-[#E5A93D] rounded-sm active:scale-95"
                                data-testid="prompter-mirror-btn"
                                title="Spiegel openen — check je looks met de front-camera"
                            >
                                <ScanFace className="h-4 w-4 mr-1.5" /> <span className="hidden sm:inline">Spiegel</span>
                            </Button>
                            {/* TTS optional toggle (off by default — most makelaars lezen zelf) */}
                            <Button
                                onClick={() => setTtsEnabled((v) => !v)}
                                variant="ghost"
                                className={`h-11 w-11 border rounded-sm ${ttsEnabled ? "border-[#E5A93D]/40 bg-[#E5A93D]/10 text-[#E5A93D]" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}
                                data-testid="prompter-tts-toggle"
                                title={ttsEnabled ? "Stem aan — klik om uit te zetten" : "Stem uit — klik om de tekst ook te laten voorlezen"}
                            >
                                {ttsEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                            </Button>
                            {ttsEnabled && (
                                <>
                                    <select
                                        value={rate}
                                        onChange={(e) => setRate(parseFloat(e.target.value))}
                                        className="h-11 bg-[#0F0F11] border border-white/10 text-sm text-zinc-300 rounded-sm px-2 focus:outline-none focus:border-[#E5A93D]"
                                        data-testid="tts-rate"
                                    >
                                        {[0.8, 0.9, 1.0, 1.1, 1.25, 1.5].map((r) => (
                                            <option key={r} value={r}>{r}×</option>
                                        ))}
                                    </select>
                                    {voices.length > 0 && (
                                        <select
                                            value={voiceURI}
                                            onChange={(e) => setVoiceURI(e.target.value)}
                                            className="h-11 bg-[#0F0F11] border border-white/10 text-sm text-zinc-300 rounded-sm px-2 max-w-[160px] focus:outline-none focus:border-[#E5A93D] hidden md:block"
                                            data-testid="tts-voice"
                                        >
                                            {voices.map((v) => (
                                                <option key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</option>
                                            ))}
                                        </select>
                                    )}
                                </>
                            )}
                            <Button onClick={() => setFontSize((s) => Math.max(20, s - 4))} variant="ghost" className="h-11 w-11 border border-white/10 text-zinc-300 hover:bg-white/5 rounded-sm" data-testid="font-smaller" aria-label="Kleiner">
                                <Minus className="h-4 w-4" />
                            </Button>
                            <div className="px-1 text-xs text-zinc-500 w-12 text-center tabular-nums">{fontSize}px</div>
                            <Button onClick={() => setFontSize((s) => Math.min(120, s + 4))} variant="ghost" className="h-11 w-11 border border-white/10 text-zinc-300 hover:bg-white/5 rounded-sm" data-testid="font-bigger" aria-label="Groter">
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Spiegel overlay — gespiegelde front-camera, fullscreen, met knijp-zoom */}
            {mirrorOpen && (
                <div
                    ref={mirrorBoxRef}
                    className="fixed inset-0 z-50 bg-black overflow-hidden flex items-center justify-center"
                    style={{ touchAction: "none" }}
                    onTouchStart={onMirrorTouchStart}
                    onTouchMove={onMirrorTouchMove}
                    onTouchEnd={onMirrorTouchEnd}
                    data-testid="mirror-overlay"
                >
                    <div
                        className="h-full w-full"
                        style={{ transform: `translate(${mirrorPan.x}px, ${mirrorPan.y}px) scale(${mirrorZoom})`, transformOrigin: "center center" }}
                        data-testid="mirror-zoom-wrap"
                    >
                        <video
                            ref={mirrorVideoRef}
                            autoPlay
                            playsInline
                            muted
                            className="h-full w-full object-cover"
                            style={{ transform: "scaleX(-1)" }}
                            data-testid="mirror-video"
                        />
                    </div>
                    <div className="absolute top-0 inset-x-0 flex items-center justify-between p-4 bg-gradient-to-b from-black/70 to-transparent">
                        <span className="inline-flex items-center gap-2 text-sm text-white/80 select-none">
                            <ScanFace className="h-4 w-4 text-[#E5A93D]" /> Spiegel — knijp om in te zoomen 🔍
                        </span>
                        <button
                            type="button"
                            onClick={closeMirror}
                            className="h-11 w-11 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white flex items-center justify-center active:scale-95 transition"
                            data-testid="mirror-close-btn"
                            aria-label="Spiegel sluiten"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                    {/* Zoomknoppen (ook handig zonder touch) + indicator */}
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setZoomTo(mirrorZoom + 0.5)}
                            className="h-11 w-11 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white flex items-center justify-center active:scale-95 transition"
                            data-testid="mirror-zoom-in"
                            aria-label="Inzoomen"
                        >
                            <Plus className="h-5 w-5" />
                        </button>
                        <span className="text-xs text-white/70 font-bold tabular-nums select-none bg-black/40 rounded-full px-2 py-0.5" data-testid="mirror-zoom-level">
                            {mirrorZoom.toFixed(1)}×
                        </span>
                        <button
                            type="button"
                            onClick={() => setZoomTo(mirrorZoom - 0.5)}
                            className="h-11 w-11 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white flex items-center justify-center active:scale-95 transition disabled:opacity-40"
                            disabled={mirrorZoom <= 1}
                            data-testid="mirror-zoom-out"
                            aria-label="Uitzoomen"
                        >
                            <Minus className="h-5 w-5" />
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={closeMirror}
                        className="absolute bottom-8 left-1/2 -translate-x-1/2 h-12 px-6 rounded-full bg-[#E5A93D] text-black font-bold active:scale-95 transition shadow-lg"
                        data-testid="mirror-done-btn"
                    >
                        Klaar — terug naar script
                    </button>
                </div>
            )}

            <style>{`
                /* Hide scrollbar during teleprompter mode for a cleaner read */
                .teleprompter-scroll { scrollbar-width: thin; scrollbar-color: #27272A transparent; }
                .teleprompter-scroll::-webkit-scrollbar { width: 6px; }
                .teleprompter-scroll::-webkit-scrollbar-thumb { background: #27272A; border-radius: 3px; }
            `}</style>
        </div>
    );
}

function ScrollTextIcon() {
    return (
        <div className="h-12 w-12 mx-auto rounded-sm bg-[#E5A93D]/10 border border-[#E5A93D]/30 flex items-center justify-center">
            <Mic2 className="h-5 w-5 text-[#E5A93D]" />
        </div>
    );
}
